import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service'; // Adjust the path as per your project structure
import { AuthTokenService } from '../services/auth-token.service';

/**
 * Set on a request whose caller shows its own error message, so this
 * interceptor doesn't add a second, generic toast on top of it.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * The readable sentence in an error body. The backend now always sends
 * {"error": "..."} (fantasyapp/errors.py); older shapes are still understood.
 */
function serverMessage(body: any): string | null {
  if (!body) return null;
  if (typeof body === 'string') return body.length < 300 && !body.trim().startsWith('<') ? body : null;
  if (Array.isArray(body)) return typeof body[0] === 'string' ? body[0] : null;
  for (const key of ['error', 'message', 'detail']) {
    if (typeof body[key] === 'string' && body[key].trim()) return body[key];
  }
  for (const value of Object.values(body)) {
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return null;
}

const MESSAGES = {
  offline: "We can't reach the server right now. Check your connection and try again.",
  badInput: 'Please check what you entered and try again.',
  signIn: 'Please sign in again.',
  forbidden: "You don't have permission to do that.",
  notFound: "We couldn't find that.",
  tooFast: "You're doing that too fast — wait a moment and try again.",
  server: 'Something went wrong on our side. Please try again in a moment.',
};

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private router: Router, private toastService: ToastService, private authTokens: AuthTokenService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const fromServer = serverMessage(error.error);
        let errorMessage = fromServer || MESSAGES.server;

        if (error.error instanceof ErrorEvent || error.status === 0) {
          // Never reached the server (offline, server down, blocked).
          errorMessage = MESSAGES.offline;
          if (!req.context.get(SKIP_ERROR_TOAST)) this.toastService.showNote(errorMessage, 'danger');
        } else {
          if (req.url.includes('geoserver')) return throwError(() => error);
          if (req.url.includes('location/favourite/')) return throwError(() => error);
          if(req.url.includes('cell-resource-details/')) return throwError(() => error);
          // Store requests refused for a missing / expired login: one "sign in"
          // message, even for requests that otherwise show their own errors.
          if (error.status === 401 && req.url.includes('/api/v1/')) {
            const signedIn = !!this.authTokens.accessToken;
            if (req.method !== 'GET' || signedIn) {
              this.toastService.showSignInNeeded(signedIn);
            }
            return throwError(() => error);
          }
          if (req.context.get(SKIP_ERROR_TOAST)) return throwError(() => error);
          switch (error.status) {
            case 400:
            case 409:
            case 422:
              errorMessage = fromServer || MESSAGES.badInput;
              this.toastService.showNote(errorMessage, 'warning');
              break;
            case 401:
              // The marketplace API (/api/v1/) is handled above: its public pages
              // make optional signed-in calls that simply return nothing when
              // signed out.
              errorMessage = MESSAGES.signIn;
              this.toastService.showNote(errorMessage, 'warning');
              this.router.navigate(['/login']); // Redirect to login page
              break;
            case 403:
              errorMessage = fromServer || MESSAGES.forbidden;
              this.toastService.showNote(errorMessage, 'danger');
              break;
            case 404:
              // Pages decide whether "not found" is worth a message.
              errorMessage = fromServer || MESSAGES.notFound;
              break;
            case 429:
              errorMessage = MESSAGES.tooFast;
              this.toastService.showNote(errorMessage, 'warning');
              break;
            default:
              errorMessage = fromServer || MESSAGES.server;
              this.toastService.showNote(errorMessage, 'danger');
          }
        }

        // Pass the server's response on (pages read err.error.error / .message
        // for the reason), with `message` set to the sentence shown above —
        // not Angular's "Http failure response for …".
        try {
          Object.defineProperty(error, 'message', { value: errorMessage, configurable: true });
        } catch {
          /* keep Angular's message */
        }
        console.error(errorMessage);
        return throwError(() => error);
      })
    );
  }
}
